import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useBoard, useConfig } from "@/hooks/useData";
import { CallTypeCombo } from "@/components/workspace/CallTypeCombo";

export function OfficerInitiatedCard({ close, onCreated, initialUnitId }) {
  const boardQ = useBoard();
  const ctQ = useConfig("call_types");
  const qc = useQueryClient();
  const units = boardQ.data?.units || [];
  const callTypes = useMemo(() => (ctQ.data || []).filter((c) => c.active !== false), [ctQ.data]);
  const [unitId, setUnitId] = useState(initialUnitId || "");
  const [callTypeId, setCallTypeId] = useState("");
  const [location, setLocation] = useState("");
  const [narrative, setNarrative] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!unitId && units.length) setUnitId((units.find((u) => u.status.category === "available") || units[0]).id); }, [units, unitId]);
  useEffect(() => { if (!callTypeId && callTypes.length) setCallTypeId((callTypes.find((c) => c.code === "TRAFFIC") || callTypes[0]).id); }, [callTypes, callTypeId]);
  const ct = callTypes.find((c) => c.id === callTypeId);
  useEffect(() => { if (!touched) setNarrative(ct?.narrative_prompt || ""); }, [ct, touched]);

  const unit = units.find((u) => u.id === unitId);
  const create = async () => {
    if (!unitId || !callTypeId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/incidents/officer-initiated", { unit_id: unitId, call_type_id: callTypeId, location_raw: location });
      if (touched && narrative.trim()) await api.post(`/incidents/${data.id}/comments`, { text: narrative.trim() });
      qc.invalidateQueries({ queryKey: ["board"] });
      toast.success(`${data.incident_number} created ACTIVE — ${unit?.call_sign} assigned${!location.trim() ? " · Location Pending" : ""}`);
      onCreated?.(data.id);
      close();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="officer-initiated-card">
      <div>
        <div className="overline-label">Officer-initiated activity</div>
        <div className="display text-lg font-semibold">Quick entry</div>
        <div className="text-xs text-muted-foreground">Creates a committed incident directly in ACTIVE with the initiating unit assigned.</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label>
          <span className="overline-label">Unit</span>
          <select data-testid="oi-unit-select" value={unitId} onChange={(e) => setUnitId(e.target.value)} className="field-input mt-0.5">
            {units.map((u) => <option key={u.id} value={u.id}>{u.call_sign} · {u.status.label}{u.incident ? ` (on ${u.incident.incident_number})` : ""}</option>)}
          </select>
        </label>
        <label>
          <span className="overline-label">Call type (confirm or change)</span>
          <div className="mt-0.5"><CallTypeCombo callTypes={callTypes} value={callTypeId} onChange={setCallTypeId} testId="oi-call-type-combo" /></div>
        </label>
      </div>
      <label className="block">
        <span className="overline-label">Location {!location.trim() && <span className="text-amber-600 dark:text-amber-300 normal-case tracking-normal" data-testid="oi-location-pending">· Location Pending until entered</span>}</span>
        <input data-testid="oi-location-input" className="field-input mt-0.5" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Leave blank if not yet radioed" />
      </label>
      <label className="block">
        <span className="overline-label">Narrative — prompts for {ct?.name || "call type"}</span>
        <textarea data-testid="oi-narrative-input" rows={5} value={narrative} onChange={(e) => { setNarrative(e.target.value); setTouched(true); }} className="field-input !h-auto mt-0.5 py-1.5 mono text-xs" />
      </label>
      {unit?.incident && <div className="text-xs text-amber-600 dark:text-amber-300" data-testid="oi-unit-busy-warning">{unit.call_sign} is currently on {unit.incident.incident_number} — it will be pulled from that call.</div>}
      <div className="flex justify-end gap-2">
        <button data-testid="oi-cancel-button" onClick={close} className="h-8 px-3 rounded-md border text-xs hover:bg-accent">Cancel</button>
        <button data-testid="oi-create-button" disabled={busy || !unitId || !callTypeId} onClick={create} className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50">Create Active incident</button>
      </div>
    </div>
  );
}
